import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { DatePicker } from 'rsuite';
import { Plus, X, AlertCircle } from 'lucide-react';
import { cobranzasAPI } from '../services/api';
import { Card, Button, Modal, Input, EmptyState, Tooltip } from './ui';
import { selectStyles } from '../utils/adminShared';
import { moneyFmt, getCurrentDate, formatDateTimeShort } from '../utils/helpers';

/**
 * PagosCard
 *
 * Card de cobranzas para CotizacionDetalle. Solo se renderiza cuando:
 *   - La cotizacion esta en estado 'aprobada'
 *   - El usuario tiene permiso 'gestion_cobranzas'
 *
 * Permite registrar pagos parciales, editar, anular (soft delete con
 * motivo) y muestra historial + saldo en vivo.
 */

const METODOS_FIJOS = [
    { value: 'transferencia', label: 'Transferencia' },
    { value: 'efectivo',      label: 'Efectivo' },
    { value: 'yape',          label: 'Yape' },
    { value: 'plin',          label: 'Plin' },
    { value: 'tarjeta',       label: 'Tarjeta' },
    { value: '__otro__',      label: 'Otro' },
];

// Devuelve el label visible para el chip de un metodo (puede ser custom)
const labelMetodo = (metodoBD) => {
    const fijo = METODOS_FIJOS.find(m => m.value === metodoBD);
    if (fijo && fijo.value !== '__otro__') return fijo.label;
    return metodoBD || '—';
};

// Mapa de colores de chip por metodo
const colorMetodo = (metodoBD) => {
    if (metodoBD === 'efectivo') return 'efectivo';
    if (metodoBD === 'transferencia') return 'transferencia';
    if (metodoBD === 'yape' || metodoBD === 'plin') return 'wallet';
    if (metodoBD === 'tarjeta') return 'tarjeta';
    return 'otro';
};

// Conversores YYYY-MM-DD <-> Date sin pasar por toISOString().
// toISOString() falla con RangeError si Date es invalido y ademas convierte
// a UTC (off-by-one en timezones negativos como Lima -05:00).
const dateFromYMD = (ymd) => {
    if (!ymd || typeof ymd !== 'string') return null;
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return null;
    const dt = new Date(y, m - 1, d);
    return isNaN(dt.getTime()) ? null : dt;
};
const dateToYMD = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const PagosCard = ({ cotizacion, onChange }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);   // { cotizacion: {...}, pagos: [...] }
    const [showRegistrar, setShowRegistrar] = useState(false);
    const [pagoAnular, setPagoAnular] = useState(null);

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await cobranzasAPI.getPagos(cotizacion.id);
            setData(res.data);
        } catch (err) {
            console.error('Error cargando pagos:', err);
            toast.error('No pudimos cargar los pagos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cotizacion.id]);

    const totales = useMemo(() => {
        if (!data) return null;
        const c = data.cotizacion;
        const pct = c.total > 0 ? Math.min(100, Math.round((c.monto_pagado / c.total) * 100)) : 0;
        return { ...c, pct };
    }, [data]);

    const handleRegistroOk = async () => {
        setShowRegistrar(false);
        await cargar();
        if (onChange) onChange();
    };

    const handleAnularOk = async () => {
        setPagoAnular(null);
        await cargar();
        if (onChange) onChange();
    };

    if (loading) {
        return (
            <Card className="mt-3">
                <Card.Body>
                    <div className="text-muted text-sm">Cargando pagos...</div>
                </Card.Body>
            </Card>
        );
    }

    if (!totales) return null;

    const moneda = totales.moneda || 'PEN';
    const estadoBadge = {
        pendiente: { label: 'Pendiente', cls: 'pagos-badge pagos-badge--pendiente' },
        parcial:   { label: 'Parcial',   cls: 'pagos-badge pagos-badge--parcial' },
        pagado:    { label: 'Pagado',    cls: 'pagos-badge pagos-badge--pagado' },
    }[totales.estado_pago] || { label: '—', cls: 'pagos-badge' };

    const noAnulados = (data.pagos || []).filter(p => !p.anulado_at);

    return (
        <>
            <Card className="mt-3">
                <Card.Header
                    title="Pagos"
                    actions={
                        totales.saldo > 0 && (
                            <Button
                                variant="primary"
                                size="sm"
                                leftIcon={<Plus size={14} />}
                                onClick={() => setShowRegistrar(true)}
                            >
                                Registrar pago
                            </Button>
                        )
                    }
                />
                <Card.Body>
                    {/* Resumen */}
                    <div className="pagos-resumen">
                        <div className="pagos-resumen-top">
                            <span className={estadoBadge.cls}>{estadoBadge.label}</span>
                            <span className="pagos-resumen-pct">{totales.pct}%</span>
                        </div>
                        <div className="pagos-progress">
                            <div
                                className={`pagos-progress-fill pagos-progress-fill--${totales.estado_pago}`}
                                style={{ width: `${totales.pct}%` }}
                            />
                        </div>
                        <div className="pagos-resumen-cifras">
                            <div className="pagos-cifra">
                                <span className="pagos-cifra-label">Total</span>
                                <span className="pagos-cifra-valor">{moneyFmt(totales.total, moneda)}</span>
                            </div>
                            <div className="pagos-cifra">
                                <span className="pagos-cifra-label">Pagado</span>
                                <span className="pagos-cifra-valor pagos-cifra-valor--ok">
                                    {moneyFmt(totales.monto_pagado, moneda)}
                                </span>
                            </div>
                            <div className="pagos-cifra">
                                <span className="pagos-cifra-label">Saldo</span>
                                <span className={`pagos-cifra-valor ${totales.saldo > 0 ? 'pagos-cifra-valor--pend' : 'pagos-cifra-valor--ok'}`}>
                                    {moneyFmt(totales.saldo, moneda)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Tabla de pagos */}
                    {(data.pagos || []).length === 0 ? (
                        <EmptyState
                            title="Sin pagos registrados"
                            description="Cuando registres el primer abono aparecerá aquí."
                            className="mt-4"
                        />
                    ) : (
                        <div className="pagos-tabla-wrap mt-4">
                            <table className="pagos-tabla">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th style={{ textAlign: 'right' }}>Monto</th>
                                        <th>Método</th>
                                        <th>Referencia</th>
                                        <th>Notas</th>
                                        <th>Registrado por</th>
                                        <th style={{ width: 60 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.pagos.map(p => {
                                        const anulado = !!p.anulado_at;
                                        return (
                                            <tr key={p.id} className={anulado ? 'pago-anulado' : ''}>
                                                <td>{formatFecha(p.fecha_pago)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                                    {moneyFmt(p.monto, moneda)}
                                                </td>
                                                <td>
                                                    <span className={`pago-metodo pago-metodo--${colorMetodo(p.metodo)}`}>
                                                        {labelMetodo(p.metodo)}
                                                    </span>
                                                </td>
                                                <td className="text-muted">{p.referencia || '—'}</td>
                                                <td className="text-muted">
                                                    {anulado ? (
                                                        <span title={`Anulado por ${p.anulado_por_nombre || '—'} el ${formatDateTimeShort(p.anulado_at)}`}>
                                                            <AlertCircle size={12} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 4, color: 'rgb(var(--danger))' }} />
                                                            {p.motivo_anulacion || 'Anulado'}
                                                        </span>
                                                    ) : (p.notas || '—')}
                                                </td>
                                                <td className="text-muted">{p.registrado_por_nombre || '—'}</td>
                                                <td>
                                                    {!anulado && (
                                                        <Tooltip content="Anular pago">
                                                            <button
                                                                type="button"
                                                                className="btn-icon delete"
                                                                onClick={() => setPagoAnular(p)}
                                                                aria-label="Anular pago"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                {noAnulados.length > 0 && (
                                    <tfoot>
                                        <tr>
                                            <td colSpan={6} style={{ textAlign: 'right', fontWeight: 600, padding: '8px 12px' }}>
                                                Total abonado
                                            </td>
                                            <td style={{ textAlign: 'right', fontWeight: 700 }}>
                                                {moneyFmt(totales.monto_pagado, moneda)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {showRegistrar && (
                <RegistrarPagoModal
                    cotizacionId={cotizacion.id}
                    total={totales.total}
                    saldo={totales.saldo}
                    moneda={moneda}
                    onClose={() => setShowRegistrar(false)}
                    onOk={handleRegistroOk}
                />
            )}

            {pagoAnular && (
                <AnularPagoModal
                    cotizacionId={cotizacion.id}
                    pago={pagoAnular}
                    moneda={moneda}
                    onClose={() => setPagoAnular(null)}
                    onOk={handleAnularOk}
                />
            )}
        </>
    );
};

// =====================================================================
// Modal: Registrar pago
// =====================================================================
const RegistrarPagoModal = ({ cotizacionId, total, saldo, moneda, onClose, onOk }) => {
    const [monto, setMonto] = useState('');
    const [metodoSelect, setMetodoSelect] = useState('transferencia');
    // Campo dual-purpose: si metodo=otro, almacena el nombre del metodo.
    // Si no, almacena la referencia (# voucher / operacion bancaria).
    const [referencia, setReferencia] = useState('');
    const [notas, setNotas] = useState('');
    const [fechaPago, setFechaPago] = useState(getCurrentDate());
    const [saving, setSaving] = useState(false);

    const esOtro = metodoSelect === '__otro__';
    const montoNum = parseFloat(monto) || 0;
    const saldoDespues = Math.max(0, saldo - montoNum);
    const esPagoCompleto = Math.abs(montoNum - saldo) < 0.01;

    const handlePagarTodo = () => {
        setMonto(String(saldo.toFixed(2)));
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (saving) return;
        if (montoNum <= 0) {
            toast.error('El monto debe ser mayor a 0');
            return;
        }
        if (montoNum > saldo + 0.01) {
            toast.error('El monto excede el saldo pendiente');
            return;
        }
        // Si es Otro -> el campo referencia trae el metodo, y no hay referencia separada.
        const metodo = esOtro ? referencia.trim() : metodoSelect;
        const refFinal = esOtro ? null : (referencia.trim() || null);
        if (!metodo) {
            toast.error(esOtro ? 'Especifica el método de pago' : 'Indica el método de pago');
            return;
        }

        setSaving(true);
        try {
            const res = await cobranzasAPI.registrarPago(cotizacionId, {
                monto: montoNum,
                metodo,
                referencia: refFinal,
                notas: notas.trim() || null,
                fecha_pago: fechaPago
            });
            toast.success(res.message || 'Pago registrado');
            onOk();
        } catch (err) {
            toast.error(err.message || 'Error al registrar pago');
        } finally {
            setSaving(false);
        }
    };

    const metodoOption = METODOS_FIJOS.find(m => m.value === metodoSelect) || METODOS_FIJOS[0];

    return (
        <Modal
            open
            onClose={onClose}
            title="Registrar pago"
            size="lg"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Registrando...' : 'Registrar pago'}
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit} className="pagos-form">
                {/* Resumen del saldo */}
                <div className="pagos-form-resumen">
                    <div>
                        <span className="text-muted text-xs">Total cotización</span>
                        <div className="font-semibold">{moneyFmt(total, moneda)}</div>
                    </div>
                    <div>
                        <span className="text-muted text-xs">Saldo pendiente</span>
                        <div className="font-semibold" style={{ color: 'rgb(var(--warning))' }}>
                            {moneyFmt(saldo, moneda)}
                        </div>
                    </div>
                </div>

                {/* Quick action: pagar todo */}
                <Button
                    type="button"
                    variant="soft"
                    size="sm"
                    onClick={handlePagarTodo}
                    className="pagos-form-pagar-todo"
                >
                    Pagar saldo completo ({moneyFmt(saldo, moneda)})
                </Button>

                {/* Monto + Fecha (DatePicker rsuite, no input date nativo) */}
                <div className="pagos-form-row">
                    <Input
                        label="Monto a abonar *"
                        type="number"
                        min="0.01"
                        step="0.01"
                        max={saldo + 0.01}
                        value={monto}
                        onChange={(e) => setMonto(e.target.value)}
                        autoFocus
                    />
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-muted">Fecha de pago *</label>
                        <DatePicker
                            value={dateFromYMD(fechaPago)}
                            onChange={(date) => setFechaPago(dateToYMD(date))}
                            format="dd/MM/yyyy"
                            oneTap
                            block
                            cleanable={false}
                            className="cot-form-datepicker"
                            placement="bottomStart"
                            container={() => document.body}
                        />
                    </div>
                </div>

                {/* Saldo en vivo */}
                {montoNum > 0 && montoNum <= saldo && (
                    <div className="pagos-form-hint">
                        {esPagoCompleto ? (
                            <span className="pagos-form-hint--ok">
                                ✓ Este pago completa el total de la cotización
                            </span>
                        ) : (
                            <>
                                Saldo después de este pago:{' '}
                                <strong>{moneyFmt(saldoDespues, moneda)}</strong>
                            </>
                        )}
                    </div>
                )}
                {montoNum > saldo + 0.01 && (
                    <div className="pagos-form-hint pagos-form-hint--err">
                        El monto excede el saldo pendiente
                    </div>
                )}

                {/* Metodo (react-select) + Referencia/Otro (campo dual-purpose) */}
                <div className="pagos-form-row">
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-muted">Método *</label>
                        <Select
                            styles={selectStyles}
                            options={METODOS_FIJOS}
                            value={metodoOption}
                            onChange={(opt) => setMetodoSelect(opt?.value || 'transferencia')}
                            placeholder="Método..."
                            isSearchable
                            noOptionsMessage={() => 'Sin opciones'}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </div>
                    <Input
                        label={esOtro ? 'Especificar método *' : 'Referencia'}
                        value={referencia}
                        onChange={(e) => setReferencia(e.target.value)}
                        placeholder={esOtro
                            ? 'Ej: Cheque, depósito agente'
                            : '# voucher / operación (opcional)'}
                        maxLength={esOtro ? 50 : 100}
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-muted">Notas (opcional)</label>
                    <textarea
                        className="bg-surface border border-line rounded-lg p-3 text-sm text-text outline-none focus:ring-2 focus:ring-cyan2/30 focus:border-cyan2 placeholder:text-muted/70"
                        rows="2"
                        value={notas}
                        onChange={(e) => setNotas(e.target.value)}
                        placeholder="Observaciones del pago"
                    />
                </div>
            </form>
        </Modal>
    );
};

// =====================================================================
// Modal: Anular pago — pide motivo obligatorio
// =====================================================================
const AnularPagoModal = ({ cotizacionId, pago, moneda, onClose, onOk }) => {
    const [motivo, setMotivo] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!motivo.trim()) {
            toast.error('El motivo es obligatorio');
            return;
        }
        setSaving(true);
        try {
            await cobranzasAPI.anularPago(cotizacionId, pago.id, motivo.trim());
            toast.success('Pago anulado');
            onOk();
        } catch (err) {
            toast.error(err.message || 'Error al anular pago');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            title="Anular pago"
            size="sm"
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
                    <Button variant="danger" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Anulando...' : 'Anular pago'}
                    </Button>
                </>
            }
        >
            <form onSubmit={handleSubmit}>
                <p className="text-sm" style={{ marginBottom: 14 }}>
                    Vas a anular el pago de <strong>{moneyFmt(pago.monto, moneda)}</strong>
                    {' '}del <strong>{formatFecha(pago.fecha_pago)}</strong>. Dejará de sumar al
                    saldo y quedará en el historial marcado como anulado.
                </p>
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-muted">Motivo de anulación *</label>
                    <textarea
                        autoFocus
                        rows="3"
                        className="bg-surface border border-line rounded-lg p-3 text-sm text-text outline-none focus:ring-2 focus:ring-danger/30 focus:border-danger placeholder:text-muted/70"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        placeholder="Ej: Cliente revirtió la transferencia"
                        maxLength={255}
                    />
                </div>
            </form>
        </Modal>
    );
};

// =====================================================================
// Helpers de formato
// =====================================================================
const formatFecha = (fecha) => {
    if (!fecha) return '—';
    const [anio, mes, dia] = String(fecha).split('T')[0].split('-');
    return `${dia}/${mes}/${anio}`;
};

export default PagosCard;
